// Application entry: providers, router, and the SSE bridge.

import { StrictMode, type ReactNode } from 'react';
import { createRoot } from 'react-dom/client';
import { MantineProvider } from '@mantine/core';
import { QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { I18nProvider } from '@/i18n/I18nProvider';
import { AppearanceProvider } from '@/theme/AppearanceProvider';
import { SessionProvider } from '@/session/SessionProvider';
import { sensingCssVariables, sensingTheme } from '@/theme/theme';
import { queryClient } from '@/query/client';
import { router } from '@/router';
import { useLiveEvents } from '@/features/events/useEventStream';
import '@/theme/global.css';

/** Connects the owner-scoped event stream to query invalidation. */
function LiveEventsBridge(): null {
  useLiveEvents();
  return null;
}

function Shell(): ReactNode {
  return (
    <QueryClientProvider client={queryClient}>
      <SessionProvider>
        <LiveEventsBridge />
        <RouterProvider router={router} />
      </SessionProvider>
    </QueryClientProvider>
  );
}

/** Root composition: theme, appearance, and language wrap everything else. */
function Root(): ReactNode {
  return (
    <MantineProvider
      defaultColorScheme="auto"
      theme={sensingTheme}
      cssVariablesResolver={sensingCssVariables}
    >
      <AppearanceProvider>
        <I18nProvider>
          <Shell />
        </I18nProvider>
      </AppearanceProvider>
    </MantineProvider>
  );
}

// A deploy replaces the previous build's files (docs/adr/0016), so a tab still
// running that build cannot load its lazy route chunks. Reload into the new
// build, at most once in ten seconds so a chunk that is really missing fails
// normally instead of reloading forever.
const CHUNK_RELOAD_KEY = 'sensing:chunk-reload';
window.addEventListener('vite:preloadError', () => {
  const last = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY) ?? 0);
  if (Date.now() - last < 10_000) return;
  sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
  window.location.reload();
});

const container = document.getElementById('root');
if (!container) {
  throw new Error('root container is missing');
}

createRoot(container).render(
  <StrictMode>
    <Root />
  </StrictMode>,
);
